// Data hooks for the maintainer-gated `/analytics` dashboard
// (analytics+postgres Phase 6). Two reads, two access tiers:
//
//   useAnalyticsOverview() — GET /api/analytics/overview. Content-free
//     aggregate (Tier-1). Fetched once on mount, mirroring the dashboard's
//     `useFetchOnce`. A 403 sets `forbidden` so the page renders the
//     access-denied state off the server gate (no role lives in the cookie).
//
//   useNamedUsage() — GET /api/analytics?scope=users. The attributed
//     drill-down (Tier-1 + Tier-2, audited). DELIBERATELY NOT fetched on
//     mount: the named fetch is the audited action (the server records the
//     access before returning any row, analytics.ts), so it only fires on the
//     explicit `reveal()` user action. The various non-200s map to distinct,
//     non-leaky page states (`notEnabled` = 404, `needsProject` = 400,
//     `forbidden` = 403) rather than a raw error string.

import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";

import {
  AnalyticsDailyPointSchema,
  AnalyticsModelRowSchema,
  AnalyticsNamedSessionRowSchema,
  AnalyticsNamedUserRowSchema,
  AnalyticsOverviewSchema,
  AnalyticsProjectRowSchema,
  AnalyticsSessionActionRowSchema,
  SessionTranscriptSchema,
  type AnalyticsSessionActionRow,
  type SessionTranscript,
} from "@loom/shared/analytics";

import { httpGet, HttpError, UnauthorizedError } from "../lib/http";

// ---------------------------------------------------------------------------
// Wire shapes — the composite the routes actually return (the shared package
// exports the per-row pieces; the envelope is route-local).
// ---------------------------------------------------------------------------

/** As-billed Anthropic cost summary for the trailing 7 UTC days — present only
 *  when the server runs the billing-reconcile job's table (postgres). `null`
 *  when the backend has no relational pool; `.nullish()` (rather than
 *  `.default()`, which would split zod's input/output types and break the
 *  `httpGet<T>` inference) keeps an older server without the `billing` key
 *  parseable during a rolling deploy — consumers treat absent as null. */
const BillingSummarySchema = z
  .object({
    days: z.array(z.object({ day: z.string(), billedUsd: z.string() }).strict()),
    totalBilledUsd: z.string(),
    totalAllCostTypesUsd: z.string(),
  })
  .strict();
export type BillingSummary = z.infer<typeof BillingSummarySchema>;

/**
 * FRESHNESS DISCLOSURE (server: `apps/server/src/analytics/projection.ts`).
 *
 * These routes read a BATCH-PROJECTED star schema refreshed by the
 * `analytics-sync` cron, never live session state — so two reads inside one
 * window are byte-identical by construction. Without the surface saying so, an
 * operator refetching a drill-down for a session they believe is stuck reads a
 * documented batch lag as a frozen ledger (which is exactly what happened).
 *
 * `.nullish()` for the same rolling-deploy reason as `billing` below: a server
 * that has not been redeployed yet omits the key, and these envelopes are
 * `.strict()`.
 */
const AnalyticsProjectionSchema = z
  .object({
    mode: z.literal("batch"),
    refreshMinutes: z.number(),
    asOf: z.string().nullable(),
  })
  .strict();
export type AnalyticsProjection = z.infer<typeof AnalyticsProjectionSchema>;

/** The server emits the block only when asked, so every request this file makes
 *  carries the flag. It is opt-in because an unconditional new key would throw
 *  in an OLD bundle's `.strict()` parse — not because the product does not want
 *  it. */
const PROJECTION_PARAM = "projection=1";

const OverviewResponseSchema = z
  .object({
    overview: AnalyticsOverviewSchema,
    projects: z.array(AnalyticsProjectRowSchema),
    daily: z.array(AnalyticsDailyPointSchema),
    /** Per-model spend breakdown (cost-descending, server-sorted). `.nullish()`
     *  for the same rolling-deploy reason as `billing`: a new bundle must not
     *  hard-fail against a not-yet-redeployed server missing the key. */
    models: z.array(AnalyticsModelRowSchema).nullish(),
    billing: BillingSummarySchema.nullish(),
    projection: AnalyticsProjectionSchema.nullish(),
  })
  .strict();
export type OverviewResponse = z.infer<typeof OverviewResponseSchema>;

const NamedResponseSchema = z
  .object({
    scope: z.literal("users"),
    orgWide: z.boolean(),
    projectId: z.number().int().nullable(),
    range: z.object({
      from: z.string().datetime().nullable(),
      to: z.string().datetime().nullable(),
    }),
    users: z.array(AnalyticsNamedUserRowSchema),
    projection: AnalyticsProjectionSchema.nullish(),
  })
  .strict();
export type NamedResponse = z.infer<typeof NamedResponseSchema>;

const SessionsResponseSchema = z
  .object({
    scope: z.literal("sessions"),
    orgWide: z.boolean(),
    projectId: z.number().int().nullable(),
    range: z.object({
      from: z.string().datetime().nullable(),
      to: z.string().datetime().nullable(),
    }),
    sessions: z.array(AnalyticsNamedSessionRowSchema),
    projection: AnalyticsProjectionSchema.nullish(),
  })
  .strict();
export type SessionsResponse = z.infer<typeof SessionsResponseSchema>;

const SessionDetailResponseSchema = z
  .object({
    scope: z.literal("session"),
    orgWide: z.boolean(),
    projectId: z.number().int(),
    session: AnalyticsNamedSessionRowSchema,
    actions: z.array(AnalyticsSessionActionRowSchema),
    projection: AnalyticsProjectionSchema.nullish(),
  })
  .strict();

// ---------------------------------------------------------------------------
// Overview (Tier-1, fetch-on-mount)
// ---------------------------------------------------------------------------

export interface OverviewState {
  readonly data: OverviewResponse | null;
  readonly loading: boolean;
  readonly error: string | null;
  /** The server 403'd — caller is not on the viewer allowlist. */
  readonly forbidden: boolean;
}

export const useAnalyticsOverview = (): OverviewState => {
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const aborted = useRef(false);

  useEffect(() => {
    aborted.current = false;
    setLoading(true);
    setError(null);
    setForbidden(false);
    httpGet(`/api/analytics/overview?${PROJECTION_PARAM}`, OverviewResponseSchema)
      .then((value) => {
        if (aborted.current) return;
        setData(value);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (aborted.current) return;
        if (err instanceof UnauthorizedError) {
          // Route loader redirects to /sign-in; keep loading, don't flash.
          return;
        }
        if (err instanceof HttpError && err.status === 403) {
          setForbidden(true);
          setLoading(false);
          return;
        }
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      aborted.current = true;
    };
  }, []);

  return { data, loading, error, forbidden };
};

// ---------------------------------------------------------------------------
// Named drill-down (Tier-2, reveal-on-demand + audited)
// ---------------------------------------------------------------------------

/** The terminal outcome of a reveal attempt — drives the named panel copy. */
export type NamedStatus =
  | "hidden"
  | "loading"
  | "ready"
  | "notEnabled" // 404 — Phase-7 precondition (LOOM_ANALYTICS_ATTRIBUTED) off
  | "needsProject" // 400 — Tier-2 viewer; org-wide reveal not permitted
  | "forbidden" // 403 — not authorized for the named view
  | "error";

export interface NamedUsageState {
  readonly status: NamedStatus;
  readonly data: NamedResponse | null;
  readonly error: string | null;
  /**
   * Fire the audited named fetch. Pass a `projectId` for the Tier-2 per-project
   * drill-down (the viewer must Maintain it); omit it for the org-wide
   * population (org-wide viewers only). Safe to call repeatedly.
   */
  readonly reveal: (projectId?: number) => void;
  /** Collapse back to the hidden state without re-querying. */
  readonly hide: () => void;
}

export const useNamedUsage = (): NamedUsageState => {
  const [status, setStatus] = useState<NamedStatus>("hidden");
  const [data, setData] = useState<NamedResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const aborted = useRef(false);

  useEffect(
    () => () => {
      aborted.current = true;
    },
    [],
  );

  const reveal = useCallback((projectId?: number) => {
    aborted.current = false;
    setStatus("loading");
    setData(null);
    setError(null);
    // With a projectId → the Tier-2 per-project read; without → the org-wide
    // population (a Tier-2-only viewer gets a 400 here → `needsProject`). The
    // fetch itself is the audited action (the server records the access before
    // returning any row), so it only fires on this explicit reveal.
    const path =
      projectId === undefined
        ? `/api/analytics?scope=users&${PROJECTION_PARAM}`
        : `/api/analytics?scope=users&projectId=${projectId}&${PROJECTION_PARAM}`;
    httpGet(path, NamedResponseSchema)
      .then((value) => {
        if (aborted.current) return;
        setData(value);
        setStatus("ready");
      })
      .catch((err: unknown) => {
        if (aborted.current) return;
        if (err instanceof HttpError) {
          if (err.status === 404) return setStatus("notEnabled");
          if (err.status === 400) return setStatus("needsProject");
          if (err.status === 403) return setStatus("forbidden");
        }
        setError(err instanceof Error ? err.message : String(err));
        setStatus("error");
      });
  }, []);

  const hide = useCallback(() => {
    setStatus("hidden");
    setData(null);
    setError(null);
  }, []);

  return { status, data, error, reveal, hide };
};

// ---------------------------------------------------------------------------
// Session drill-down (Tier-2, reveal-on-demand + audited) — the action ledger.
// Same access shape as the named view: the list (`?scope=sessions`) and each
// expanded timeline (`?scope=session`) are separately audited reads.
// ---------------------------------------------------------------------------

export interface SessionTimelineState {
  readonly status: "loading" | "ready" | "error";
  readonly actions: ReadonlyArray<AnalyticsSessionActionRow>;
  readonly error: string | null;
}

export interface SessionsUsageState {
  readonly status: NamedStatus;
  readonly data: SessionsResponse | null;
  readonly error: string | null;
  /** Fire the audited session-list fetch (see `useNamedUsage.reveal`). */
  readonly reveal: (projectId?: number) => void;
  /** Collapse back to the hidden state without re-querying. */
  readonly hide: () => void;
  /** Per-session action timelines, loaded on row expand. */
  readonly timelines: Readonly<Record<string, SessionTimelineState>>;
  /** Fetch one session's action timeline (audited). Idempotent per session. */
  readonly loadTimeline: (sessionId: string) => void;
}

export const useSessionUsage = (): SessionsUsageState => {
  const [status, setStatus] = useState<NamedStatus>("hidden");
  const [data, setData] = useState<SessionsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [timelines, setTimelines] = useState<
    Record<string, SessionTimelineState>
  >({});
  const aborted = useRef(false);

  useEffect(
    () => () => {
      aborted.current = true;
    },
    [],
  );

  const reveal = useCallback((projectId?: number) => {
    aborted.current = false;
    setStatus("loading");
    setData(null);
    setError(null);
    setTimelines({});
    const path =
      projectId === undefined
        ? `/api/analytics?scope=sessions&${PROJECTION_PARAM}`
        : `/api/analytics?scope=sessions&projectId=${projectId}&${PROJECTION_PARAM}`;
    httpGet(path, SessionsResponseSchema)
      .then((value) => {
        if (aborted.current) return;
        setData(value);
        setStatus("ready");
      })
      .catch((err: unknown) => {
        if (aborted.current) return;
        if (err instanceof HttpError) {
          if (err.status === 404) return setStatus("notEnabled");
          if (err.status === 400) return setStatus("needsProject");
          if (err.status === 403) return setStatus("forbidden");
        }
        setError(err instanceof Error ? err.message : String(err));
        setStatus("error");
      });
  }, []);

  const hide = useCallback(() => {
    setStatus("hidden");
    setData(null);
    setError(null);
    setTimelines({});
  }, []);

  const loadTimeline = useCallback(
    (sessionId: string) => {
      if (timelines[sessionId]) return;
      setTimelines((prev) => ({
        ...prev,
        [sessionId]: { status: "loading", actions: [], error: null },
      }));
      httpGet(
        `/api/analytics?scope=session&sessionId=${encodeURIComponent(sessionId)}&${PROJECTION_PARAM}`,
        SessionDetailResponseSchema,
      )
        .then((value) => {
          if (aborted.current) return;
          setTimelines((prev) => ({
            ...prev,
            [sessionId]: { status: "ready", actions: value.actions, error: null },
          }));
        })
        .catch((err: unknown) => {
          if (aborted.current) return;
          setTimelines((prev) => ({
            ...prev,
            [sessionId]: {
              status: "error",
              actions: [],
              error: err instanceof Error ? err.message : String(err),
            },
          }));
        });
    },
    [timelines],
  );

  return { status, data, error, reveal, hide, timelines, loadTimeline };
};

// ---------------------------------------------------------------------------
// Chat transcript (Tier-3, its own page, audited per read)
// ---------------------------------------------------------------------------

const TranscriptResponseSchema = z
  .object({
    scope: z.literal("transcript"),
    transcript: SessionTranscriptSchema,
  })
  .strict();

/**
 * `notAvailable` covers BOTH 404 legs on purpose — a session from before the
 * chat log existed, one past its 30-day TTL, one wiped by `/reset`, and a
 * deployment with no chat store at all. The server does not distinguish them
 * and neither does the page: the honest sentence is "there is no transcript
 * for this session", and inventing a reason would be guessing.
 */
export type TranscriptStatus =
  | "loading"
  | "ready"
  | "notAvailable"
  | "forbidden"
  | "error";

export interface TranscriptState {
  readonly status: TranscriptStatus;
  readonly data: SessionTranscript | null;
  readonly error: string | null;
}

/**
 * Fetch one session's transcript. Unlike the reveal-gated panels on the
 * dashboard this fires on mount — the page IS the explicit action, and the
 * server audits the read before it answers.
 */
export const useSessionTranscript = (sessionId: string): TranscriptState => {
  const [status, setStatus] = useState<TranscriptStatus>("loading");
  const [data, setData] = useState<SessionTranscript | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let aborted = false;
    setStatus("loading");
    setData(null);
    setError(null);
    httpGet(
      `/api/analytics?scope=transcript&sessionId=${encodeURIComponent(sessionId)}`,
      TranscriptResponseSchema,
    )
      .then((value) => {
        if (aborted) return;
        setData(value.transcript);
        setStatus("ready");
      })
      .catch((err: unknown) => {
        if (aborted) return;
        if (err instanceof UnauthorizedError) return; // loader redirects
        if (err instanceof HttpError) {
          if (err.status === 404) return setStatus("notAvailable");
          if (err.status === 403) return setStatus("forbidden");
        }
        setError(err instanceof Error ? err.message : String(err));
        setStatus("error");
      });
    return () => {
      aborted = true;
    };
  }, [sessionId]);

  return { status, data, error };
};
